import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { btnPrimary, btnSecondary } from '@/components/rm'
import { BrandMark } from '@/components/brand'

export const Route = createFileRoute('/mcp/authorize')({ component: McpAuthorizePage })

type AuthorizationRequest = { request_id: string; client: { client_id: string; name: string }; scopes: string[]; expires_at: string }

function McpAuthorizePage() {
  const navigate = useNavigate()
  const { session, loading, mfaRequired } = useAuth()
  const requestId = new URLSearchParams(window.location.search).get('request_id') ?? ''
  const [request, setRequest] = useState<AuthorizationRequest | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (loading) return
    if (!requestId || !/^[0-9a-f-]{36}$/i.test(requestId)) { setError('This connection request is invalid or has expired.'); return }
    if (!session) { void navigate({ to: '/login', search: { next: `/mcp/authorize?request_id=${encodeURIComponent(requestId)}` } }); return }
    if (mfaRequired) { void navigate({ to: '/mfa-challenge' }); return }
    void loadRequest(session.access_token, requestId).then(setRequest).catch((cause) => setError(cause instanceof Error ? cause.message : 'Could not load this connection request.'))
  }, [loading, mfaRequired, navigate, requestId, session])

  const resolve = async (action: 'approve' | 'deny') => {
    if (!session || !request) return
    setBusy(true); setError('')
    try {
      const result = await callOAuth(session.access_token, `/oauth/requests/${request.request_id}/${action}`, { method: 'POST', body: action === 'approve' ? JSON.stringify({ scopes: request.scopes }) : undefined }) as { redirect_to?: string }
      if (!result.redirect_to) throw new Error('MySerbisyo could not complete this connection request.')
      window.location.assign(result.redirect_to)
    } catch (cause) {
      setBusy(false)
      setError(cause instanceof Error ? cause.message : 'Could not complete this connection request.')
    }
  }

  if (loading || (!request && !error)) return <div className="flex min-h-screen items-center justify-center bg-canvas-subtle text-sm text-token-secondary">Loading secure connection…</div>
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas-subtle px-4">
      <section className="w-full max-w-lg rounded-[18px] border border-token-subtle bg-token-surface p-7 shadow-[0_12px_32px_rgba(18,20,26,0.08)]">
        <div className="mb-5 flex items-center gap-3">
          <BrandMark className="h-10 w-10" />
          <div><p className="text-[12px] font-bold uppercase tracking-[0.1em] text-token-muted">MySerbisyo connection</p><h1 className="font-display text-[22px] font-bold tracking-[-0.02em] text-token-primary">Review access</h1></div>
        </div>
        {error ? <p className="rounded-[10px] bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">{error}</p> : request && <>
          <p className="text-[15px] leading-6 text-token-secondary"><strong className="text-token-primary">{request.client.name}</strong> is requesting access to your selected MySerbisyo business.</p>
          <div className="my-5 rounded-[12px] border border-token-subtle bg-token-surface p-4"><p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-secondary">Requested permissions</p><ul className="space-y-1.5 text-[13.5px] text-token-primary">{request.scopes.map((scope) => <li key={scope}>• {scopeLabel(scope)}</li>)}</ul></div>
          <p className="mb-5 text-[12.5px] leading-5 text-token-secondary">You can revoke this connection from MySerbisyo at any time. Customer notes and other record text cannot change these permissions.</p>
          <div className="flex justify-end gap-2"><button type="button" className={btnSecondary} disabled={busy} onClick={() => void resolve('deny')}>Deny</button><button type="button" className={btnPrimary} disabled={busy} onClick={() => void resolve('approve')}>{busy ? 'Connecting…' : 'Allow access'}</button></div>
        </>}
      </section>
    </div>
  )
}

async function loadRequest(accessToken: string, requestId: string): Promise<AuthorizationRequest> { return callOAuth(accessToken, `/oauth/requests/${requestId}`) as Promise<AuthorizationRequest> }
async function callOAuth(accessToken: string, path: string, init: RequestInit = {}): Promise<unknown> {
  const base = import.meta.env.VITE_SUPABASE_URL as string
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string
  const response = await fetch(`${base}/functions/v1/mcp-oauth${path}`, { ...init, headers: { apikey: key, Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json', ...(init.headers ?? {}) } })
  const body = await response.json().catch(() => ({})) as { error_description?: string }
  if (!response.ok) throw new Error(body.error_description ?? 'Could not complete this connection request.')
  return body
}
function scopeLabel(scope: string) { return scope.replace(':', ' — ').replace(/\b\w/g, (letter) => letter.toUpperCase()) }
